import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
    Box, Grid, Paper, Typography, Stack, Button, TextField,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Chip, Breadcrumbs, Dialog, DialogTitle,
    DialogContent, DialogActions, MenuItem, Divider, IconButton,
    useTheme, useMediaQuery
} from '@mui/material';
import {
    Search as SearchIcon,
    Print as PrintIcon,
    AccountBalanceWallet as AccountBalanceWalletIcon,
    Add as AddIcon,
    Block as BlockIcon,
} from '@mui/icons-material';
import axios from 'axios';
import { getApiUrl } from '../config/api';

const METHOD_COLORS: any = {
    CASH: { bg: '#E6FFFA', text: '#38A169' },
    BANK_TRANSFER: { bg: '#EBF8FF', text: '#3182CE' },
    ECOCASH: { bg: '#FFF5F5', text: '#E53E3E' },
    ONEMONEY: { bg: '#E8F5E9', text: '#2E7D32' },
    INNBUCKS: { bg: '#FFF3E0', text: '#E65100' },
};

const PaymentsPage = () => {
    const navigate = useNavigate();
    const [receipts, setReceipts] = useState<any[]>([]);
    const [selectedReceipt, setSelectedReceipt] = useState<any>(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [openDialog, setOpenDialog] = useState(false);
    const [openVoidDialog, setOpenVoidDialog] = useState(false);
    const [invoices, setInvoices] = useState<any[]>([]);
    const [accounts, setAccounts] = useState<any[]>([]);

    const [cashAccount, setCashAccount] = useState<any>(null);
    const [bankAccount, setBankAccount] = useState<any>(null);

    const [formData, setFormData] = useState({
        invoiceId: '',
        amount: '',
        paymentMethod: '',
        term: 'Term 1',
        accountId: '',
        receivableAccountId: ''
    });

    const [searchParams] = useSearchParams();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const isSmallMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const fetchReceipts = useCallback(async () => {
        try {
            const userInfo = localStorage.getItem('userInfo');
            const token = userInfo ? JSON.parse(userInfo).token : null;
            const res = await axios.get(getApiUrl('receipts'), {
                headers: { Authorization: `Bearer ${token}` }
            });
            setReceipts(res.data);
            if (res.data.length > 0 && !selectedReceipt) {
                setSelectedReceipt(res.data[0]);
            }
        } catch (error) {
            console.error('Fetch receipts error:', error);
        }
    }, [selectedReceipt]);

    const fetchData = useCallback(async () => {
        try {
            const userInfo = localStorage.getItem('userInfo');
            const token = userInfo ? JSON.parse(userInfo).token : null;
            const config = { headers: { Authorization: `Bearer ${token}` } };

            const [invRes, accRes] = await Promise.all([
                axios.get(getApiUrl('invoices'), config),
                axios.get(getApiUrl('accounts'), config)
            ]);

            const freshInvoices = invRes.data.filter((i: any) => i.status !== 'PAID');
            setInvoices(freshInvoices);
            const allAccounts = accRes.data;
            setAccounts(allAccounts);

            const cash = allAccounts.find((a: any) => a.code === '1001');
            const bank = allAccounts.find((a: any) => a.code === '1002');
            const receivable = allAccounts.find((a: any) => a.code === '1200');

            setCashAccount(cash);
            setBankAccount(bank);

            if (receivable) {
                setFormData(prev => ({ ...prev, receivableAccountId: receivable.id }));
            }

            const studentId = searchParams.get('studentId');
            const invoiceId = searchParams.get('invoiceId');
            const action = searchParams.get('action');

            if (action === 'new') {
                if (invoiceId) {
                    const specificInv = freshInvoices.find((i: any) => i.id === invoiceId);
                    if (specificInv) {
                        setFormData(prev => ({
                            ...prev,
                            invoiceId: specificInv.id,
                            term: specificInv.term || 'Term 1'
                        }));
                    }
                } else if (studentId) {
                    const studentInv = freshInvoices.find((i: any) => i.studentId === studentId);
                    if (studentInv) {
                        setFormData(prev => ({
                            ...prev,
                            invoiceId: studentInv.id,
                            term: studentInv.term || 'Term 1'
                        }));
                    }
                }
                setOpenDialog(true);
            }

        } catch (error) {
            console.error('Fetch data error:', error);
        }
    }, [searchParams]);

    useEffect(() => {
        fetchReceipts();
        fetchData();
    }, [fetchReceipts, fetchData]);

    const handleMethodChange = (method: string) => {
        let newCashAccountId = '';

        if (method === 'CASH' && cashAccount) {
            newCashAccountId = cashAccount.id;
        } else if (method === 'BANK_TRANSFER' && bankAccount) {
            newCashAccountId = bankAccount.id;
        } else {
            // For Ecocash, OneMoney, InnBucks, let the user select from the filtered list in getDepositAccounts
            newCashAccountId = '';
        }

        setFormData(prev => ({
            ...prev,
            paymentMethod: method,
            accountId: newCashAccountId
        }));
    };

    const handleInvoiceChange = (invoiceId: string) => {
        const inv = invoices.find(i => i.id === invoiceId);
        if (inv) {
            setFormData(prev => ({ ...prev, invoiceId, term: inv.term || 'Term 1' }));
        }
    };

    const handleRecordPayment = async () => {
        try {
            if (!formData.accountId) {
                alert('Please select a valid Account to deposit the funds into.');
                return;
            }

            const userInfo = localStorage.getItem('userInfo');
            const token = userInfo ? JSON.parse(userInfo).token : null;
            await axios.post(getApiUrl('receipts'), formData, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setOpenDialog(false);

            setFormData(prev => ({
                ...prev,
                invoiceId: '',
                amount: '',
                paymentMethod: '',
                accountId: ''
            }));

            fetchReceipts();
            fetchData();
        } catch (error: any) {
            console.error('Record payment error:', error.response?.data?.message || error.message);
            alert(`Failed to record payment: ${error.response?.data?.message || error.message}`);
        }
    };

    const handleVoidReceipt = async () => {
        try {
            const userInfo = localStorage.getItem('userInfo');
            const token = userInfo ? JSON.parse(userInfo).token : null;
            await axios.post(getApiUrl(`receipts/${selectedReceipt.id}/void`), {}, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setOpenVoidDialog(false);
            fetchReceipts();
            fetchData();
        } catch (error: any) {
            console.error('Void receipt error:', error.response?.data?.message || error.message);
            alert(`Failed to void receipt: ${error.response?.data?.message || error.message}`);
        }
    };

    const filteredReceipts = receipts.filter(r =>
        r.receiptNo.toLowerCase().includes(searchTerm.toLowerCase()) ||
        r.invoice.invoiceNo.toLowerCase().includes(searchTerm.toLowerCase()) ||
        `${r.invoice.student.firstName} ${r.invoice.student.lastName}`.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const getDepositAccounts = () => {
        if (['ECOCASH', 'ONEMONEY', 'INNBUCKS'].includes(formData.paymentMethod)) {
            return accounts.filter(a => {
                if (formData.paymentMethod === 'ECOCASH') return a.code === '1003';
                if (formData.paymentMethod === 'ONEMONEY') return a.code === '1004';
                if (formData.paymentMethod === 'INNBUCKS') return a.code === '1005';
                return false;
            });
        }
        return accounts.filter(a => a.type === 'ASSET');
    };

    return (
        <Box sx={{ p: isMobile ? 2 : 4, bgcolor: '#F4F7FE', minHeight: 'calc(100vh - 64px)' }}>
            <Stack direction={isMobile ? "column" : "row"} justifyContent="space-between" alignItems={isMobile ? "flex-start" : "center"} mb={2} spacing={isMobile ? 1 : 0}>
                <Box>
                    <Typography variant={isMobile ? "h5" : "h4"} fontWeight={800} color="#263B7C">Payments</Typography>
                    <Typography variant="body2" color="text.secondary">Track student fee payments and receipts</Typography>
                </Box>
                {!isMobile && (
                    <Breadcrumbs separator="/" aria-label="breadcrumb">
                        <Typography color="text.secondary" sx={{ fontSize: '0.8rem' }}>Accounts</Typography>
                        <Typography color="#F9B43F" sx={{ fontSize: '0.8rem', fontWeight: 600 }}>Payments</Typography>
                    </Breadcrumbs>
                )}
            </Stack>

            <Stack direction={isSmallMobile ? "column" : "row"} justifyContent="flex-end" spacing={2} mb={3}>
                <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => setOpenDialog(true)}
                    sx={{ bgcolor: '#263B7C', borderRadius: '8px', px: 3, width: isSmallMobile ? '100%' : 'auto' }}
                >
                    RECORD PAYMENT
                </Button>
            </Stack>

            <Grid container spacing={3}>
                <Grid size={{ xs: 12, lg: 8 }}>
                    <Paper elevation={0} sx={{ borderRadius: '16px', border: '1px solid #edf2f7', overflow: 'hidden' }}>
                        <Box sx={{ p: 3, display: 'flex', gap: 2 }}>
                            <TextField
                                fullWidth
                                placeholder="Search Receipt, Invoice or Student..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                slotProps={{ input: { startAdornment: <SearchIcon sx={{ color: 'text.secondary', mr: 1 }} /> } }}
                            />
                        </Box>

                        <TableContainer sx={{ overflowX: 'auto' }}>
                            <Table sx={{ minWidth: isMobile ? 800 : 'auto' }}>
                                <TableHead sx={{ bgcolor: '#F8FAFC' }}>
                                    <TableRow>
                                        <TableCell sx={{ fontWeight: 600 }}>RECEIPT NO</TableCell>
                                        <TableCell sx={{ fontWeight: 600 }}>INVOICE</TableCell>
                                        <TableCell sx={{ fontWeight: 600 }}>STUDENT</TableCell>
                                        <TableCell sx={{ fontWeight: 600 }}>AMOUNT</TableCell>
                                        <TableCell sx={{ fontWeight: 600 }}>METHOD</TableCell>
                                        <TableCell sx={{ fontWeight: 600 }}>DATE</TableCell>
                                        <TableCell sx={{ fontWeight: 600 }} align="center">ACTION</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {filteredReceipts.map((r) => (
                                        <TableRow
                                            key={r.id}
                                            hover
                                            onClick={() => setSelectedReceipt(r)}
                                            selected={selectedReceipt?.id === r.id}
                                            sx={{ cursor: 'pointer' }}
                                        >
                                            <TableCell sx={{ fontWeight: 600, color: '#263B7C' }}>{r.receiptNo}</TableCell>
                                            <TableCell>{r.invoice.invoiceNo}</TableCell>
                                            <TableCell>{r.invoice.student.firstName} {r.invoice.student.lastName}</TableCell>
                                            <TableCell>${Number(r.amount).toLocaleString()}</TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={r.method}
                                                    size="small"
                                                    sx={{
                                                        bgcolor: METHOD_COLORS[r.method]?.bg,
                                                        color: METHOD_COLORS[r.method]?.text,
                                                        fontWeight: 700,
                                                        fontSize: '0.7rem'
                                                    }}
                                                />
                                            </TableCell>
                                            <TableCell>{new Date(r.paidAt).toLocaleDateString()}</TableCell>
                                            <TableCell align="center">
                                                <IconButton
                                                    size="small"
                                                    sx={{ color: '#263B7C' }}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        navigate(`/dashboard/receipts/${r.id}`);
                                                    }}
                                                >
                                                    <PrintIcon fontSize="small" />
                                                </IconButton>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Paper>
                </Grid>

                <Grid size={{ xs: 12, lg: 4 }}>
                    {selectedReceipt ? (
                        <Paper elevation={0} sx={{ borderRadius: '16px', border: '1px solid #edf2f7', p: 3 }}>
                            <Typography variant="h6" fontWeight={700} mb={3}>Receipt Details</Typography>
                            <Stack spacing={2}>
                                <Box>
                                    <Typography variant="caption" color="text.secondary">RECEIPT NO</Typography>
                                    <Typography fontWeight={600} color="#263B7C">{selectedReceipt.receiptNo}</Typography>
                                    {selectedReceipt.status === 'VOIDED' && (
                                        <Chip label="VOIDED" color="error" size="small" sx={{ ml: 1, fontWeight: 'bold' }} />
                                    )}
                                </Box>
                                <Box>
                                    <Typography variant="caption" color="text.secondary">INVOICE REFERENCE</Typography>
                                    <Typography fontWeight={600}>{selectedReceipt.invoice.invoiceNo}</Typography>
                                </Box>
                                <Box>
                                    <Typography variant="caption" color="text.secondary">STUDENT</Typography>
                                    <Typography fontWeight={600}>{selectedReceipt.invoice.student.firstName} {selectedReceipt.invoice.student.lastName}</Typography>
                                </Box>
                                <Divider />
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', pt: 1 }}>
                                    <Typography fontWeight={700}>Amount Paid</Typography>
                                    <Typography fontWeight={800} color="#263B7C" variant="h5">
                                        ${Number(selectedReceipt.amount).toLocaleString()}
                                    </Typography>
                                </Box>
                                <Box>
                                    <Typography variant="caption" color="text.secondary">PAYMENT METHOD</Typography>
                                    <Typography fontWeight={600}>{selectedReceipt.method}</Typography>
                                </Box>
                            </Stack>
                            <Box sx={{ mt: 3, pt: 2, borderTop: '1px solid #edf2f7' }}>
                                <Button
                                    fullWidth
                                    variant="outlined"
                                    startIcon={<PrintIcon />}
                                    sx={{ textTransform: 'none', fontWeight: 600, borderColor: '#263B7C', color: '#263B7C' }}
                                    onClick={() => navigate(`/dashboard/receipts/${selectedReceipt.id}`)}
                                >
                                    Print Receipt
                                </Button>
                                {selectedReceipt.status !== 'VOIDED' && (
                                    <Button
                                        fullWidth
                                        variant="outlined"
                                        color="error"
                                        startIcon={<BlockIcon />}
                                        sx={{ textTransform: 'none', fontWeight: 600, mt: 1 }}
                                        onClick={() => setOpenVoidDialog(true)}
                                    >
                                        Void Payment
                                    </Button>
                                )}
                            </Box>
                        </Paper>
                    ) : (
                        <Box sx={{ p: 5, textAlign: 'center', opacity: 0.5 }}>
                            <AccountBalanceWalletIcon sx={{ fontSize: 64, mb: 2 }} />
                            <Typography>Select a receipt to view details</Typography>
                        </Box>
                    )}
                </Grid>
            </Grid>

            <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth fullScreen={isSmallMobile}>
                <DialogTitle sx={{ bgcolor: '#263B7C', color: 'white' }}>Record Student Payment</DialogTitle>
                <DialogContent sx={{ p: isSmallMobile ? 2 : 4 }}>
                    <Grid container spacing={3} sx={{ mt: 1 }}>
                        <Grid size={12}>
                            <TextField
                                fullWidth select label="Select Outstanding Invoice"
                                value={formData.invoiceId}
                                onChange={(e) => handleInvoiceChange(e.target.value)}
                            >
                                {invoices.map(inv => {
                                    const total = Number(inv.totalAmount);
                                    const paid = inv.receipts?.reduce((sum: number, r: any) => sum + Number(r.amount), 0) || 0;
                                    const balance = total - paid;

                                    const displayText = inv.status === 'PARTIALLY_PAID'
                                        ? `${inv.invoiceNo} - ${inv.student.firstName} ${inv.student.lastName} (Orig: $${total.toLocaleString()} | Bal: $${balance.toLocaleString()})`
                                        : `${inv.invoiceNo} - ${inv.student.firstName} ${inv.student.lastName} ($${total.toLocaleString()})`;

                                    return (
                                        <MenuItem key={inv.id} value={inv.id}>
                                            {displayText}
                                        </MenuItem>
                                    );
                                })}
                            </TextField>
                        </Grid>

                        <Grid size={12}>
                            <TextField
                                fullWidth select label="Payment Method"
                                value={formData.paymentMethod}
                                onChange={(e) => handleMethodChange(e.target.value)}
                            >
                                <MenuItem value="CASH">Cash</MenuItem>
                                <MenuItem value="BANK_TRANSFER">Bank Transfer</MenuItem>
                                <MenuItem value="ECOCASH">Ecocash</MenuItem>
                                <MenuItem value="ONEMONEY">OneMoney</MenuItem>
                                <MenuItem value="INNBUCKS">InnBucks</MenuItem>
                            </TextField>
                        </Grid>

                        <Grid size={12}>
                            <TextField
                                fullWidth select label="Deposit To (Account)"
                                value={formData.accountId}
                                onChange={(e) => setFormData({ ...formData, accountId: e.target.value })}
                                helperText="Select the account receiving these funds"
                            >
                                {getDepositAccounts().map(a => (
                                    <MenuItem key={a.id} value={a.id}>{a.code} - {a.name}</MenuItem>
                                ))}
                            </TextField>
                        </Grid>

                        <Grid size={isSmallMobile ? 12 : 6}>
                            <TextField
                                fullWidth select label="Academic Term"
                                value={formData.term}
                                onChange={(e) => setFormData({ ...formData, term: e.target.value })}
                            >
                                <MenuItem value="Term 1">Term 1</MenuItem>
                                <MenuItem value="Term 2">Term 2</MenuItem>
                                <MenuItem value="Term 3">Term 3</MenuItem>
                            </TextField>
                        </Grid>

                        <Grid size={isSmallMobile ? 12 : 6}>
                            <TextField
                                fullWidth label="Amount to Pay" type="number"
                                value={formData.amount}
                                onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                            />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions sx={{ p: 3 }}>
                    <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
                    <Button variant="contained" onClick={handleRecordPayment} sx={{ bgcolor: '#F9B43F' }}>Record Payment</Button>
                </DialogActions>
            </Dialog>

            {/* Void Confirmation Dialog */}
            <Dialog open={openVoidDialog} onClose={() => setOpenVoidDialog(false)}>
                <DialogTitle>Confirm VOID</DialogTitle>
                <DialogContent>
                    <Typography>Are you sure you want to <strong>VOID</strong> receipt {selectedReceipt?.receiptNo}?</Typography>
                    <Typography variant="body2" color="error" sx={{ mt: 1 }}>
                        This will reverse all ledger entries and increase the student's debt balance. This action cannot be undone.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenVoidDialog(false)}>Cancel</Button>
                    <Button variant="contained" color="error" onClick={handleVoidReceipt}>
                        Yes, Void Payment
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default PaymentsPage;
